import { cn } from '@/lib/cn';
import { AdminLink } from './adminRoute';
import type { AdminSubTabState } from './adminRoute';

/**
 * The tab strip for a setting's tabs (useAdminSubTab). Each tab is a real
 * link to its own URL, so it can be opened in a new browser tab.
 * `level={0}`: underline tabs under the page header. `level={1}`: a smaller
 * segmented control for a sub-tab (Messaging → SMS → Connection / Templates),
 * so the two levels never look like one row.
 */
export default function AdminTabs({ state, level = 0, className }: { state: AdminSubTabState; level?: 0 | 1; className?: string }) {
  if (state.tabs.length < 2 && level === 1) return null;

  if (level === 1) {
    return (
      <div role="tablist" className={cn('mb-4 inline-flex flex-wrap gap-0.5 rounded-lg bg-muted p-[3px]', className)}>
        {state.tabs.map((t) => {
          const on = t.id === state.active;
          return (
            <AdminLink
              key={t.id}
              to={t.to}
              role="tab"
              aria-selected={on}
              className={cn(
                'rounded-md px-3 py-1 text-[12.5px] font-semibold no-underline transition-colors',
                on ? 'bg-card text-ink shadow-card' : 'text-ink-muted hover:text-ink'
              )}
            >
              {t.label}
            </AdminLink>
          );
        })}
      </div>
    );
  }

  return (
    <div role="tablist" className={cn('mb-4 flex gap-1 overflow-x-auto border-b border-border', className)}>
      {state.tabs.map((t) => {
        const on = t.id === state.active;
        return (
          <AdminLink
            key={t.id}
            to={t.to}
            role="tab"
            aria-selected={on}
            className={cn(
              '-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-[13px] font-semibold no-underline transition-colors',
              on ? 'border-teal text-teal' : 'border-transparent text-ink-muted hover:text-ink'
            )}
          >
            {t.label}
          </AdminLink>
        );
      })}
    </div>
  );
}
